import React from "react"
import SetupFlowLayout from "../../Components/SetupFlowLayout"
import { Paragraph } from "../../Components/Paragraph"
import Label from "../../Components/Forms/Label"
import TextField from "../../Components/Forms/FieldTypes/TextField"
import { PRIVACY_POLICY_URL } from "../../constants/textConstants"
import Button from "../../Components/Button"
import { useOutletContextPlanActions } from "../../hooks/useOutletContextPlanActions"
import { useOutletContextPlan } from "../../hooks/useOutletContextPlan"
import { FieldArray } from "formik"
import { isStepLocked } from "../../lib/plan"
import { PLAN_STATUS } from "../../constants/planStatus"
import LockedStep from "../../Components/LockedStep"
import { useNavigate } from "react-router-dom"
import { getUserFullName } from "../../lib/utils"
import { Pill } from "../../Components/Pill"
import {MoreInfoVideoModal} from "../../Components/MoreInfoVideoModal";
import {INVITE_MENTORS_RESOURCE_URL} from "../../constants/resources";

/**
 * SendPlan
 *
 * Multi-step flow for finalizing and sending the mentorship plan.
 * This is step 5 (final step) of the setup checklist.
 */
const SendPlan = () => {
  const { handleDownloadPlan, error, loading, handleSendPlan } = useOutletContextPlanActions()
  const { guide, guardians, student, mentors, currentStep, plan, guideIsGuardian } =
    useOutletContextPlan()
  const navigate = useNavigate()
  const [submitting, setSubmitting] = React.useState(false)
  const steps = [
    {
      id: "invite-members",
      encouragement: "📧",
      encouragementClassName: "text-3xl",
      title: "Send the Plan",
      renderCustomContent: values => {
        return (
          <div className="mx-auto flex max-w-xl flex-col items-center justify-center gap-2 pb-8 text-center">
            <Paragraph>
              {student?.first_name ? student.first_name : "Your Student"}'s Mentor Circle is nearly
              complete!
            </Paragraph>
            <Paragraph>
              Share the plan with their Mentors and start walking together over the year ahead!
            </Paragraph>

            <div className="-mb-6 mt-6 w-full space-y-3">
              <div className="flex flex-col items-center gap-2">
                <div className="flex w-full max-w-72 flex-row justify-between">
                  <Label name={"studentEmail"}>{getUserFullName(student)} </Label>
                  <Pill variant="vision" as="span" className="ml-6">
                    Student
                  </Pill>
                </div>
                <TextField
                  name={"studentEmail"}
                  placeholder={"Student's email address"}
                  type="email"
                  className="mx-auto max-w-72"
                />
              </div>
              {guideIsGuardian || guardians.length === 0 ? null : (
                <FieldArray name={"guardians"}>
                  {({ push, form }) => {
                    const guardians = form.values.guardians || []
                    return (
                      <div className="w-full space-y-3 pt-3">
                        {guardians.map((guardian, index) => (
                          <div key={index} className="flex flex-col items-center gap-3">
                            <div className="flex w-full max-w-72 flex-row justify-between">
                              <Label>{getUserFullName(guardian)} </Label>
                              <Pill variant="gifts" as="span" className="ml-6">
                                Guardian #{index + 1}
                              </Pill>
                            </div>
                            <TextField
                              name={`guardians.${index}.email`}
                              placeholder={`Guardian ${index + 1}'s email address`}
                              type="email"
                              className="mx-auto max-w-72"
                            />
                          </div>
                        ))}
                      </div>
                    )
                  }}
                </FieldArray>
              )}

              <FieldArray name={"mentors"}>
                {({ push, form }) => {
                  const mentors = form.values.mentors || []
                  return (
                    <div className="w-full space-y-6 pt-3">
                      {mentors.map((mentor, index) => (
                        <div key={index} className="flex flex-col items-center gap-1">
                          <div className="flex w-full max-w-72 flex-row justify-between">
                            <Label>{getUserFullName(mentor)} </Label>
                            <Pill variant="circle" as="span" className="ml-6">
                              Mentor #{index + 1}
                            </Pill>
                          </div>
                          <TextField
                            name={`mentors.${index}.email`}
                            placeholder={`Mentor ${index + 1}'s email address`}
                            type="email"
                            className="mx-auto max-w-72"
                          />
                        </div>
                      ))}
                    </div>
                  )
                }}
              </FieldArray>
              <div className="flex flex-col items-center gap-2 pt-3">
                <div className="flex w-full max-w-72 flex-row justify-between">
                  <Label name={"guideEmail"}>{getUserFullName(guide)} </Label>
                  <Pill variant="brand" as="span" className="ml-6">
                    Guide
                  </Pill>
                </div>
                <TextField
                  name={"guideEmail"}
                  placeholder={"Guide's email address"}
                  type="email"
                  className="mx-auto max-w-72"
                />
              </div>
            </div>
          </div>
        )
      },
    },
  ]

  const handleComplete = async (values, stepId) => {
    try {
      setSubmitting(true)

      const response = await handleSendPlan(values)

      console.log("Plan sent successfully:", response.data)
      navigate("../plan-complete")
    } catch (error) {
      console.error("Error sending plan:", error)

      if (error.response?.data?.message) {
        alert(`Error: ${error.response.data.message}`)
      } else {
        alert("An error occurred while sending the plan. Please try again.")
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (loading || !student || !mentors || !guide || !guardians) {
    return <div>Loading...</div>
  }

  if (isStepLocked(PLAN_STATUS.SEND_PLAN, currentStep)) {
    return <LockedStep />
  }

  return (
    <SetupFlowLayout
      steps={steps}
      initialValues={{
        studentEmail: student?.email ?? "",
        guardians: guardians ?? [],
        guideEmail: guide?.email ?? "",
        mentors: mentors,
        sendOptions: [],
        finalMessage: "",
      }}
      onComplete={handleComplete}
      onBack={() => {
        navigate(`/plan/${plan?.token}/checklist`)
        return true
      }}
      alwaysShowBackButton={true}
      stepId="send-plan"
      completeButtonText="Send The Plan"
      showProgress={false}
      customContentAfterButton={
        <div className="mt-6 w-full space-y-3">
          <Paragraph className="text-sm text-gray-600 text-center">
            It doesn't end here.{" "}
            <MoreInfoVideoModal
              src={INVITE_MENTORS_RESOURCE_URL}
              text="Learn more"
              postText=" about keeping the momentum going."
              title={"How to Keep the Momentum Going After Completing a Mentor Circle Gathering"}
            />
          </Paragraph>
          <div className="text-center text-sm">
            By inviting Mentors, you agree to our{" "}
            <a href={PRIVACY_POLICY_URL} className="text-gray-600 hover:underline">
              Privacy Policy
            </a>
          </div>
          <div className="flex justify-center">
            <Button type="button" onClick={handleDownloadPlan} variant="secondary-ghost">
              Download the Plan instead
            </Button>
          </div>
        </div>
      }
    />
  )
}

export default SendPlan
